import React, { useState, useEffect } from 'react';
import { Clock, ArrowLeft, GitBranch, FileDiff, CheckCircle, XCircle } from 'lucide-react';
import { protocolService } from '../../services/protocolService';
import { useProtocol } from '../../hooks/useProtocols';
import authService from '../../services/authService';
import Modal from '../ui/Modal';
import VersionDiffModal from './VersionDiffModal';
import './ProtocolVersionControl.css';

const ProtocolVersionControl = ({ protocolId, onBack }) => {
  const [versionHistory, setVersionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [versionToRollback, setVersionToRollback] = useState(null);
  const [rollbackSummary, setRollbackSummary] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const { protocol } = useProtocol(protocolId);

  useEffect(() => {
    // Get current user ID
    const user = authService.getCurrentUser();
    if (user) {
      setCurrentUserId(user.uid);
    }
  }, []);

  useEffect(() => {
    loadVersionHistory();
  }, [protocolId]);

  const loadVersionHistory = async () => {
    try {
      setLoading(true);
      const history = await protocolService.getVersionHistory(protocolId);
      setVersionHistory(history);
    } catch (error) {
      console.error('Error loading version history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompareVersions = (version1, version2) => {
    setSelectedVersions([version1, version2]);
    setShowDiffModal(true);
  };

  const handleRollback = async () => {
    if (!versionToRollback || !rollbackSummary.trim()) {
      return;
    }

    try {
      if (!currentUserId) {
        console.error('User not authenticated');
        return;
      }
      
      await protocolService.rollbackToVersion(
        protocolId,
        versionToRollback.new_protocol_id,
        currentUserId,
        rollbackSummary
      );
      setShowRollbackModal(false);
      setVersionToRollback(null);
      setRollbackSummary('');
      loadVersionHistory();
    } catch (error) {
      console.error('Error rolling back version:', error);
    }
  };

  const getVersionStatus = (version) => {
    if (version.new_protocol_id === protocolId) {
      return { status: 'current', label: 'Current', icon: CheckCircle, color: '#10b981' };
    }
    return { status: 'previous', label: 'Previous', icon: Clock, color: '#6b7280' };
  };

  if (loading) {
    return <div className="loading-container">Loading version history...</div>;
  }

  return (
    <div className="protocol-version-control">
      <div className="version-control-header">
        <button onClick={onBack} className="btn btn-secondary btn-icon">
          <ArrowLeft size={16} />
          Back
        </button>
        <h2>Version Control: {protocol?.name || 'Protocol'}</h2>
      </div>

      <div className="current-version-info">
        <div className="version-badge current">
          <GitBranch size={18} />
          <span>Current Version: {protocol?.version || '1.0'}</span>
        </div>
        {protocol?.protocol_code && (
          <div className="protocol-code">Code: {protocol.protocol_code}</div>
        )}
      </div>

      <div className="version-history-section">
        <h3>Version History</h3>
        {versionHistory.length === 0 ? (
          <div className="no-versions">
            <p>No version history available. This is the first version.</p>
          </div>
        ) : (
          <div className="version-list">
            {versionHistory.map((version, index) => {
              const versionInfo = getVersionStatus(version);
              const StatusIcon = versionInfo.icon;
              
              return (
                <div key={version.id} className={`version-item ${versionInfo.status}`}>
                  <div className="version-header">
                    <div className="version-left">
                      <StatusIcon size={18} color={versionInfo.color} />
                      <div className="version-details">
                        <div className="version-number">
                          Version {version.new_version}
                          {versionInfo.status === 'current' && (
                            <span className="current-badge">Current</span>
                          )}
                        </div>
                        <div className="version-date">
                          {version.created_at?.toDate?.()?.toLocaleDateString() || 
                           new Date(version.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="version-actions">
                      {versionInfo.status !== 'current' && (
                        <button
                          onClick={() => {
                            setVersionToRollback(version);
                            setShowRollbackModal(true);
                          }}
                          className="btn btn-primary btn-sm"
                        >
                          Rollback
                        </button>
                      )}
                      <button
                        onClick={() => {
                          // Compare selected version with current version
                          const currentVersion = versionHistory.find(v => v.new_protocol_id === protocolId) || 
                                                 (protocol && { new_protocol_id: protocolId, new_version: protocol.version });
                          if (currentVersion) {
                            handleCompareVersions(version, currentVersion);
                          }
                        }}
                        className="btn btn-secondary btn-sm"
                      >
                        <FileDiff size={14} />
                        Compare with Current
                      </button>
                    </div>
                  </div>
                  {version.change_summary && (
                    <div className="change-summary">
                      <strong>Changes:</strong> {version.change_summary}
                    </div>
                  )}
                  <div className="version-meta">
                    Created by: {version.created_by || 'System'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Rollback Confirmation Modal */}
      <Modal
        isOpen={showRollbackModal}
        onClose={() => {
          setShowRollbackModal(false);
          setVersionToRollback(null);
          setRollbackSummary('');
        }}
        title="Rollback to Previous Version"
        size="medium"
      >
        <div className="modal-form">
          <div className="form-group">
            <label>Rolling back to Version {versionToRollback?.new_version}</label>
            <p className="warning-text">
              ⚠️ This will create a new version based on the selected version. 
              The current version will remain in history.
            </p>
          </div>
          <div className="form-group">
            <label htmlFor="rollback-summary">Change Summary *</label>
            <textarea
              id="rollback-summary"
              value={rollbackSummary}
              onChange={(e) => setRollbackSummary(e.target.value)}
              className="field-input"
              rows={3}
              placeholder="Explain why you're rolling back to this version..."
              required
            />
          </div>
          <div className="form-actions">
            <button
              type="button"
              onClick={() => {
                setShowRollbackModal(false);
                setVersionToRollback(null);
                setRollbackSummary('');
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRollback}
              className="btn btn-primary"
              disabled={!rollbackSummary.trim()}
            >
              Confirm Rollback
            </button>
          </div>
        </div>
      </Modal>

      {/* Version Diff Modal */}
      <VersionDiffModal
        isOpen={showDiffModal}
        onClose={() => {
          setShowDiffModal(false);
          setSelectedVersions([]);
        }}
        version1={selectedVersions[0]}
        version2={selectedVersions[1]}
        protocolId={protocolId}
      />
    </div>
  );
};

export default ProtocolVersionControl;

